import React from 'react';
import { createPortal } from 'react-dom';
import { X, Leaf, AlertTriangle, ShoppingCart } from 'lucide-react';
import OptimizedImage from './OptimizedImage';
import './ConfirmModal.css';

function ConfirmModal({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title, 
  message, 
  confirmText = 'OK', 
  cancelText = 'Cancel', 
  type = 'info',
  product = null // Optional product object with image, name, price, quantity, variation
}) {
  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const hasProductInfo = product && (product.image || product.name || product.price);

  return createPortal(
    <div className="confirm-modal-overlay" onClick={handleOverlayClick}>
      <div className={`confirm-modal ${hasProductInfo ? 'confirm-modal-with-product' : ''}`} onClick={(e) => e.stopPropagation()}>
        <button
          className="confirm-modal-close"
          onClick={onClose}
          aria-label="Close"
          type="button"
        >
          <X size={20} />
        </button>
        
        <div className="confirm-modal-content">
          {hasProductInfo ? (
            <>
              {/* Product Image */}
              {product.image && (
                <div className="confirm-modal-product-image">
                  <OptimizedImage
                    src={product.image}
                    alt={product.name || 'Product'}
                    className="confirm-modal-image"
                    sizes="200px"
                    aspectRatio="1"
                    priority={true}
                  />
                </div>
              )}

              {/* Product Info Section */}
              <div className="confirm-modal-product-info">
                {product.name && (
                  <h3 className="confirm-modal-product-name">{product.name}</h3>
                )}
                
                {product.variation && (
                  <div className="confirm-modal-variation">
                    {Object.entries(product.variation).map(([key, value]) => (
                      <span key={key} className="variation-badge">
                        {key}: {String(value)}
                      </span>
                    ))}
                  </div>
                )}

                <div className="confirm-modal-product-details">
                  {product.quantity && (
                    <div className="confirm-modal-detail-item">
                      <span className="detail-label">Quantity:</span>
                      <span className="detail-value">{product.quantity}</span>
                    </div>
                  )}
                  {product.price && (
                    <div className="confirm-modal-detail-item">
                      <span className="detail-label">Price:</span>
                      <span className="detail-value">₱{Number(product.price).toFixed(2)}</span>
                    </div>
                  )}
                  {product.quantity && product.price && (
                    <div className="confirm-modal-detail-item total">
                      <span className="detail-label">Total:</span>
                      <span className="detail-value">₱{(Number(product.quantity) * Number(product.price)).toFixed(2)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Title and Message */}
              {title && (
                <h3 className="confirm-modal-title">
                  <ShoppingCart size={20} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                  {title}
                </h3>
              )}
              
              {message && (
                <p className="confirm-modal-message">{message}</p>
              )}
            </>
          ) : (
            <>
              <div className={`confirm-modal-icon-wrapper ${type === 'warning' ? 'warning' : ''}`}>
                {type === 'warning' ? (
                  <AlertTriangle size={32} className="confirm-modal-icon" />
                ) : (
                  <Leaf size={32} className="confirm-modal-icon" />
                )}
              </div>
              
              {title && (
                <h3 className="confirm-modal-title">{title}</h3>
              )}
              
              {message && (
                <p className="confirm-modal-message">{message}</p>
              )}
            </>
          )}
          
          <div className="confirm-modal-actions">
            <button
              className="confirm-modal-btn confirm-modal-btn-cancel"
              onClick={onClose}
              type="button"
            >
              {cancelText}
            </button>
            <button
              className={`confirm-modal-btn confirm-modal-btn-confirm ${type === 'warning' ? 'warning' : ''}`}
              onClick={() => {
                onConfirm();
                onClose();
              }}
              type="button"
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default ConfirmModal;

